import React from 'react';
import { Search, Download, Save, AlertCircle, Sparkles, CheckCircle, XCircle } from 'lucide-react';
import { useFoodContext } from '../context';
import { FoodCard, Legend, Button, Input } from '../components';
import { getAllCategories } from '../utils/categories';
import { getCategoryColor, getCategoryEmoji } from '../utils/categories';
import { CloudOff } from 'lucide-react';

/**
 * Search Page Component - Main food search and management
 */
const SearchPage = () => {
  const {
    filteredFoods,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    foods,
    exportFoods,
    googleDrive,
    aiEstimate,
    isLoading
  } = useFoodContext();

  const categories = getAllCategories();

  const handleExport = () => {
    exportFoods('donnees_glycemiques.json');
  };

  const handleManualSync = () => {
    googleDrive.manualSync();
  };

  const handleAIEstimate = async () => {
    if (searchTerm.trim()) {
      await aiEstimate.estimateFood(searchTerm.trim());
    }
  };

  const handleAddAIFood = () => {
    const success = aiEstimate.addEstimatedFood();
    if (success) {
      // Success message will be handled by context
      console.log('✅ Aliment IA ajouté avec succès');
    }
  };

  // Show AI option when no food name matches the search term exactly
  const hasExactMatch = filteredFoods.some(f =>
    f.name.toLowerCase().trim() === searchTerm.trim().toLowerCase()
  );
  const shouldShowAIOption = searchTerm.trim().length > 1 &&
                             !hasExactMatch &&
                             !aiEstimate.showEstimation &&
                             aiEstimate.isAvailable;

  return (
    <div className="space-y-6">
      {/* Search Controls */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex flex-col lg:flex-row gap-4 mb-4">
          <div className="flex-1">
            <Input
              placeholder="Rechercher un aliment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search className="w-5 h-5" />}
              size="md"
            />
          </div>
          
          <div className="lg:w-64">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleExport}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter
            </Button>
            
            {googleDrive.isConnected && (
              <Button
                onClick={handleManualSync}
                disabled={isLoading}
                loading={isLoading}
                variant="success"
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <CloudOff className="w-4 h-4" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isLoading ? 'Sync...' : 'Sync'}
              </Button>
            )}
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          <strong>{filteredFoods.length}</strong> aliment(s) trouvé(s) sur{' '}
          <strong>{foods.length}</strong> total
        </div>
      </div>

      {/* Legend */}
      <Legend />

      {/* AI banner — shown above results when partial matches exist but no exact match */}
      {shouldShowAIOption && filteredFoods.length > 0 && (
        <div className="flex items-center justify-between gap-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-purple-700">
            <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <span>
              Pas de correspondance exacte pour <strong>"{searchTerm}"</strong> — l'IA peut l'estimer comme aliment distinct.
            </span>
          </div>
          <Button
            onClick={handleAIEstimate}
            disabled={aiEstimate.isEstimating}
            loading={aiEstimate.isEstimating}
            variant="primary"
            size="sm"
            className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            <Sparkles className="w-4 h-4" />
            {aiEstimate.isEstimating ? 'Estimation…' : 'Estimer'}
          </Button>
        </div>
      )}

      {/* Food Grid */}
      {filteredFoods.length > 0 ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFoods.map(food => (
              <FoodCard
                key={food.id}
                food={food}
                showActions={true}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              Aucun aliment trouvé
            </h3>
            <p className="text-gray-500 mb-4">
              Essayez de modifier vos critères de recherche
            </p>

            {/* AI Estimation Option — full card when no results at all */}
            {shouldShowAIOption && (
              <div className="mt-6 max-w-md mx-auto">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    <h4 className="text-lg font-semibold text-purple-800">
                      Estimation IA disponible
                    </h4>
                  </div>
                  <p className="text-sm text-purple-700 mb-4">
                    L'aliment "{searchTerm}" n'est pas dans notre base.
                    Notre IA peut estimer ses valeurs nutritionnelles.
                  </p>
                  <Button
                    onClick={handleAIEstimate}
                    disabled={aiEstimate.isEstimating}
                    loading={aiEstimate.isEstimating}
                    variant="primary"
                    size="lg"
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    <Sparkles className="w-5 h-5" />
                    {aiEstimate.isEstimating ? 'Estimation en cours...' : 'Estimer avec l\'IA'}
                  </Button>
                  <p className="text-xs text-purple-600 mt-3">
                    🤖 Estimation automatique via Mistral AI (avec fallback Groq)
                  </p>
                </div>
              </div>
            )}

            {/* AI Service Not Available */}
            {!aiEstimate.isAvailable && searchTerm.trim().length > 0 && (
              <div className="mt-6 max-w-md mx-auto">
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <p className="text-sm text-amber-700">
                    💡 <strong>Astuce :</strong> L'estimation IA n'est pas configurée.
                    Configurez REACT_APP_MISTRAL_API_KEY ou REACT_APP_GROQ_API_KEY pour activer cette fonctionnalité.
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* AI Estimation Result — outside the grid/empty conditional so it shows in both cases */}
      {aiEstimate.showEstimation && aiEstimate.estimatedFood && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-600" />
                Estimation IA
              </h3>
              <Button onClick={aiEstimate.clearEstimation} variant="ghost" size="sm">
                <XCircle className="w-4 h-4" />
              </Button>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-lg text-gray-800 mb-3">
                {aiEstimate.estimatedFood.name}
              </h4>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-sm text-purple-600 font-medium">IG</div>
                  <div className="text-2xl font-bold text-blue-600">{aiEstimate.estimatedFood.gi}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-purple-600 font-medium">Glucides</div>
                  <div className="text-2xl font-bold text-green-600">{aiEstimate.estimatedFood.carbs}g</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-purple-600 font-medium">CG</div>
                  <div className="text-2xl font-bold text-orange-600">{aiEstimate.estimatedFood.gl}</div>
                </div>
              </div>
              <div className="text-center mb-3">
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getCategoryColor(aiEstimate.estimatedFood.category)}`}>
                  {getCategoryEmoji(aiEstimate.estimatedFood.category)} Impact {aiEstimate.estimatedFood.category}
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm mb-2">
                <span className="text-gray-600">Confiance:</span>
                <span className={`font-medium ${
                  aiEstimate.estimatedFood.confidence === 'élevée' ? 'text-green-600' :
                  aiEstimate.estimatedFood.confidence === 'moyenne' ? 'text-yellow-600' :
                  'text-orange-600'
                }`}>
                  {aiEstimate.estimatedFood.confidence}
                </span>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h5 className="font-medium text-blue-800 mb-2">📝 Explication</h5>
              <p className="text-sm text-blue-700">{aiEstimate.estimatedFood.explanation}</p>
            </div>

            {aiEstimate.estimatedFood.validation?.warnings?.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-4 mb-4">
                <h5 className="font-medium text-amber-800 mb-2">⚠️ Avertissements</h5>
                <ul className="text-sm text-amber-700 space-y-1">
                  {aiEstimate.estimatedFood.validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="bg-red-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{aiEstimate.estimatedFood.warning}</span>
              </p>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleAddAIFood} variant="success" size="lg" className="flex-1">
                <CheckCircle className="w-5 h-5" /> Ajouter à ma base
              </Button>
              <Button onClick={() => aiEstimate.retryEstimation(searchTerm)} variant="outline" size="lg">
                🔄 Réessayer
              </Button>
            </div>

            <div className="mt-4 text-center text-xs text-gray-500">
              Estimé par {aiEstimate.estimatedFood.estimatedBy}
            </div>
          </div>
        </div>
      )}

      {/* AI Error Display */}
      {aiEstimate.error && (
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h5 className="font-medium text-red-800 mb-1">Erreur d'estimation</h5>
                <p className="text-sm text-red-700">{aiEstimate.error}</p>
                <Button
                  onClick={() => aiEstimate.retryEstimation(searchTerm)}
                  variant="outline" size="sm"
                  className="mt-3 text-red-600 border-red-300"
                >
                  Réessayer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;